import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

interface Question {
  id: string
  text: string
  category: string
  subcategory: string
  weight: number
}

interface Answer {
  questionId: string
  value: "yes" | "no" | "partial" | "not_sure"
}

interface CategoryScore {
  name: string
  score: number
  maxScore: number
  percentage: number
  gaps: string[]
}

interface AssessmentResults {
  overall: number
  categories: CategoryScore[]
  riskLevel: "low" | "medium" | "high" | "critical"
  gaps: string[]
}

interface ContactInfo {
  name: string
  email: string
  organization: string
  phone: string
}

interface PDFData {
  contactInfo: ContactInfo
  orgType: string | null
  orgTitle: string
  results: AssessmentResults
  questions: Question[]
  answers: Answer[]
  assessmentDate: Date
}

export function generateComplianceChecklistPDF(data: PDFData) {
  const { contactInfo, orgTitle, results, questions, answers, assessmentDate } = data

  // Create PDF document
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20

  // Colors
  const primaryBlue = [14, 165, 233] as [number, number, number] // #0EA5E9
  const darkGray = [15, 23, 42] as [number, number, number] // #0F172A
  const mediumGray = [100, 116, 139] as [number, number, number] // #64748B
  const lightGray = [226, 232, 240] as [number, number, number] // #E2E8F0
  const green = [22, 163, 74] as [number, number, number] // #16A34A
  const lightGreen = [220, 252, 231] as [number, number, number] // #DCFCE7
  const yellow = [202, 138, 4] as [number, number, number] // #CA8A04
  const lightYellow = [254, 249, 195] as [number, number, number] // #FEF9C3
  const red = [220, 38, 38] as [number, number, number] // #DC2626
  const lightRed = [254, 226, 226] as [number, number, number] // #FEE2E2

  // Helper to get risk level color
  const getRiskColor = (level: string): [number, number, number] => {
    switch (level) {
      case "low": return green
      case "medium": return yellow
      case "high": return [234, 88, 12] as [number, number, number] // Orange
      default: return red
    }
  }

  // Helper to draw a checkbox
  const drawCheckbox = (x: number, y: number, checked: boolean, color: [number, number, number]) => {
    const size = 4
    doc.setDrawColor(...color)
    doc.setLineWidth(0.5)
    doc.rect(x, y - size + 1, size, size)

    if (checked) {
      doc.setDrawColor(...color)
      doc.setLineWidth(0.8)
      // Draw checkmark
      doc.line(x + 0.8, y - 1, x + 1.8, y)
      doc.line(x + 1.8, y, x + 3.5, y - 2.5)
    }
  }

  let yPos = margin

  // === HEADER ===
  doc.setFillColor(...primaryBlue)
  doc.rect(0, 0, pageWidth, 45, "F")

  // Logo/Title
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(24)
  doc.setFont("helvetica", "bold")
  doc.text("HIPAA Ready Check", margin, 25)

  doc.setFontSize(12)
  doc.setFont("helvetica", "normal")
  doc.text("HIPAA Compliance Checklist", margin, 35)

  yPos = 60

  // === ORGANIZATION INFO ===
  doc.setTextColor(...darkGray)
  doc.setFontSize(18)
  doc.setFont("helvetica", "bold")
  doc.text(contactInfo.organization || "Your Organization", margin, yPos)
  yPos += 8

  doc.setFontSize(10)
  doc.setTextColor(...mediumGray)
  doc.setFont("helvetica", "normal")
  doc.text(`${orgTitle} Assessment`, margin, yPos)
  yPos += 5
  doc.text(`Generated: ${assessmentDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  })}`, margin, yPos)
  yPos += 5
  if (contactInfo.name) {
    doc.text(`Prepared for: ${contactInfo.name}`, margin, yPos)
    yPos += 5
  }

  yPos += 10

  // === OVERALL SCORE BOX ===
  const scoreBoxWidth = pageWidth - (margin * 2)
  const scoreBoxHeight = 45
  doc.setFillColor(240, 249, 255) // Light blue bg
  doc.roundedRect(margin, yPos, scoreBoxWidth, scoreBoxHeight, 3, 3, "F")

  // Score circle simulation
  const scoreCenterX = margin + 35
  const scoreCenterY = yPos + scoreBoxHeight / 2
  doc.setFillColor(...getRiskColor(results.riskLevel))
  doc.circle(scoreCenterX, scoreCenterY, 16, "F")
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(14)
  doc.setFont("helvetica", "bold")
  doc.text(`${results.overall}%`, scoreCenterX, scoreCenterY + 4, { align: "center" })

  // Score text
  doc.setTextColor(...darkGray)
  doc.setFontSize(14)
  doc.setFont("helvetica", "bold")
  doc.text("Overall Compliance Score", margin + 60, yPos + 15)

  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(...mediumGray)
  const riskLabels: Record<string, string> = {
    low: "Low Risk - Good compliance posture",
    medium: "Medium Risk - Some gaps need attention",
    high: "High Risk - Significant gaps identified",
    critical: "Critical Risk - Immediate action required"
  }
  doc.text(riskLabels[results.riskLevel], margin + 60, yPos + 25)

  // Category scores
  let catX = margin + 60
  const catY = yPos + 38
  results.categories.forEach((cat) => {
    const shortName = cat.name.replace(" Safeguards", "")
    const scoreColor = cat.percentage >= 80 ? green : cat.percentage >= 60 ? yellow : red
    doc.setTextColor(...darkGray)
    doc.setFontSize(8)
    doc.text(`${shortName}: `, catX, catY)
    doc.setTextColor(...scoreColor)
    doc.text(`${cat.percentage}%`, catX + doc.getTextWidth(`${shortName}: `), catY)
    catX += 50
  })

  yPos += scoreBoxHeight + 15

  // === COMPLIANCE CHECKLIST ===
  doc.setTextColor(...darkGray)
  doc.setFontSize(14)
  doc.setFont("helvetica", "bold")
  doc.text("Compliance Checklist", margin, yPos)
  yPos += 4

  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(...mediumGray)
  doc.text("Items to complete are at the top. Completed items are at the bottom of each section.", margin, yPos)
  yPos += 10

  // Group questions by category
  const categories = ["Administrative Safeguards", "Physical Safeguards", "Technical Safeguards"]

  categories.forEach((categoryName) => {
    const categoryQuestions = questions.filter(q => q.category === categoryName)
    if (categoryQuestions.length === 0) return

    // Sort questions: non-compliant first, then partial, then compliant at bottom
    const sortedQuestions = categoryQuestions.sort((a, b) => {
      const answerA = answers.find(ans => ans.questionId === a.id)
      const answerB = answers.find(ans => ans.questionId === b.id)

      const getPriority = (answer: typeof answerA) => {
        if (!answer || answer.value === "no") return 0 // Non-compliant first
        if (answer.value === "not_sure") return 1 // Needs review
        if (answer.value === "partial") return 2 // In progress
        return 3 // Compliant last
      }

      return getPriority(answerA) - getPriority(answerB)
    })

    // Count items by status
    const notCompliant = sortedQuestions.filter(q => {
      const a = answers.find(ans => ans.questionId === q.id)
      return !a || a.value === "no"
    }).length
    const needsReview = sortedQuestions.filter(q => {
      const a = answers.find(ans => ans.questionId === q.id)
      return a?.value === "not_sure"
    }).length
    const inProgress = sortedQuestions.filter(q => {
      const a = answers.find(ans => ans.questionId === q.id)
      return a?.value === "partial"
    }).length
    const compliant = sortedQuestions.filter(q => {
      const a = answers.find(ans => ans.questionId === q.id)
      return a?.value === "yes"
    }).length

    // Check if we need a new page
    if (yPos > pageHeight - 60) {
      doc.addPage()
      yPos = margin
    }

    // Category header
    doc.setFillColor(...primaryBlue)
    doc.rect(margin, yPos, pageWidth - margin * 2, 10, "F")
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(10)
    doc.setFont("helvetica", "bold")
    doc.text(categoryName, margin + 3, yPos + 7)

    // Stats on the right
    doc.setFontSize(8)
    const statsText = `${compliant} done | ${inProgress} in progress | ${notCompliant + needsReview} to do`
    doc.text(statsText, pageWidth - margin - 3, yPos + 7, { align: "right" })
    yPos += 14

    // Build table data with proper sorting
    const tableData = sortedQuestions.map(q => {
      const answer = answers.find(a => a.questionId === q.id)
      let status: "done" | "progress" | "review" | "todo" = "todo"

      if (answer?.value === "yes") {
        status = "done"
      } else if (answer?.value === "partial") {
        status = "progress"
      } else if (answer?.value === "not_sure") {
        status = "review"
      }

      return { text: q.text, status }
    })

    // Draw checklist items manually for better control
    tableData.forEach((item, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - 15) {
        doc.addPage()
        yPos = margin
      }

      const isCompleted = item.status === "done"
      const isInProgress = item.status === "progress"
      const needsReview = item.status === "review"

      // Row background for completed items
      if (isCompleted) {
        doc.setFillColor(...lightGreen)
        doc.rect(margin, yPos - 4, pageWidth - margin * 2, 10, "F")
      } else if (isInProgress) {
        doc.setFillColor(...lightYellow)
        doc.rect(margin, yPos - 4, pageWidth - margin * 2, 10, "F")
      } else if (needsReview) {
        doc.setFillColor(248, 250, 252) // Very light gray
        doc.rect(margin, yPos - 4, pageWidth - margin * 2, 10, "F")
      } else {
        doc.setFillColor(...lightRed)
        doc.rect(margin, yPos - 4, pageWidth - margin * 2, 10, "F")
      }

      // Draw border
      doc.setDrawColor(...lightGray)
      doc.setLineWidth(0.1)
      doc.rect(margin, yPos - 4, pageWidth - margin * 2, 10)

      // Checkbox
      const checkboxColor = isCompleted ? green : isInProgress ? yellow : needsReview ? mediumGray : red
      drawCheckbox(margin + 3, yPos + 2, isCompleted, checkboxColor)

      // Text
      doc.setFont("helvetica", isCompleted ? "normal" : "normal")
      doc.setFontSize(8)

      if (isCompleted) {
        doc.setTextColor(...green)
      } else if (isInProgress) {
        doc.setTextColor(...yellow)
      } else if (needsReview) {
        doc.setTextColor(...mediumGray)
      } else {
        doc.setTextColor(...darkGray)
      }

      // Truncate text if too long
      const maxWidth = pageWidth - margin * 2 - 45
      let displayText = item.text
      while (doc.getTextWidth(displayText) > maxWidth && displayText.length > 10) {
        displayText = displayText.slice(0, -4) + "..."
      }
      doc.text(displayText, margin + 12, yPos + 2)

      // Status label on the right
      doc.setFontSize(7)
      let statusLabel = "TO DO"
      let statusColor = red
      if (isCompleted) {
        statusLabel = "DONE"
        statusColor = green
      } else if (isInProgress) {
        statusLabel = "IN PROGRESS"
        statusColor = yellow
      } else if (needsReview) {
        statusLabel = "REVIEW"
        statusColor = mediumGray
      }
      doc.setTextColor(...statusColor)
      doc.text(statusLabel, pageWidth - margin - 3, yPos + 2, { align: "right" })

      yPos += 10
    })

    yPos += 8
  })

  // === PRIORITY ACTIONS ===
  if (yPos > pageHeight - 80) {
    doc.addPage()
    yPos = margin
  }

  // Find non-compliant items
  const nonCompliantItems = questions
    .filter(q => {
      const answer = answers.find(a => a.questionId === q.id)
      return !answer || answer.value === "no" || answer.value === "not_sure"
    })
    .slice(0, 5) // Top 5 priority items

  if (nonCompliantItems.length > 0) {
    yPos += 5
    doc.setTextColor(...darkGray)
    doc.setFontSize(14)
    doc.setFont("helvetica", "bold")
    doc.text("Priority Actions", margin, yPos)
    yPos += 6

    doc.setFontSize(9)
    doc.setFont("helvetica", "normal")
    doc.setTextColor(...mediumGray)
    doc.text("Address these items first to improve your compliance posture:", margin, yPos)
    yPos += 8

    nonCompliantItems.forEach((item, index) => {
      if (yPos > pageHeight - 20) {
        doc.addPage()
        yPos = margin
      }

      doc.setTextColor(...red)
      doc.setFontSize(9)
      doc.setFont("helvetica", "bold")
      doc.text(`${index + 1}.`, margin, yPos)
      doc.setTextColor(...darkGray)
      doc.setFont("helvetica", "normal")

      // Word wrap for long text
      const lines = doc.splitTextToSize(item.text, pageWidth - margin * 2 - 10)
      doc.text(lines, margin + 8, yPos)
      yPos += lines.length * 5 + 3
    })
  }

  // === FOOTER ===
  const addFooter = (pageNum: number, totalPages: number) => {
    doc.setFontSize(8)
    doc.setTextColor(...mediumGray)
    doc.text(
      `Page ${pageNum} of ${totalPages} | HIPAA Ready Check | hipaareadycheck.com`,
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    )
  }

  // === LAST PAGE - NEXT STEPS ===
  doc.addPage()
  yPos = margin

  doc.setFillColor(...primaryBlue)
  doc.rect(0, 0, pageWidth, 35, "F")
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(18)
  doc.setFont("helvetica", "bold")
  doc.text("Next Steps", margin, 22)

  yPos = 50

  doc.setTextColor(...darkGray)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("How to Use This Checklist", margin, yPos)
  yPos += 8

  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(...mediumGray)
  const nextStepsText = [
    "1. Work through items marked 'TO DO' starting from the top of each section",
    "2. Mark items as 'In Progress' as you begin addressing them",
    "3. Document your remediation efforts for audit purposes",
    "4. Retake the assessment in 90 days to measure progress",
  ]
  nextStepsText.forEach(text => {
    doc.text(text, margin, yPos)
    yPos += 7
  })

  yPos += 10

  // HIPAA Ready Pro promo
  doc.setFillColor(240, 249, 255)
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, 65, 3, 3, "F")

  doc.setTextColor(...primaryBlue)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.text("Need Help Completing Your Checklist?", margin + 5, yPos + 12)

  doc.setTextColor(...darkGray)
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  const promoLines = [
    "Upgrade to HIPAA Ready Pro for:",
    "  - Automated compliance tracking and monitoring",
    "  - AI-generated policies and procedures",
    "  - Step-by-step remediation guidance",
    "  - Expert support with 24-hour response time",
    "",
    "Visit hipaareadycheck.com/hipaa-ready-pro to learn more"
  ]
  let promoY = yPos + 22
  promoLines.forEach(line => {
    doc.text(line, margin + 5, promoY)
    promoY += 5.5
  })

  // Add footers to all pages
  const totalPages = doc.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    addFooter(i, totalPages)
  }

  // === DISCLAIMER on last page ===
  doc.setPage(totalPages)
  yPos = pageHeight - 35
  doc.setFontSize(7)
  doc.setTextColor(...mediumGray)
  const disclaimer = "Disclaimer: This checklist is for informational purposes only and does not constitute legal advice. It does not guarantee HIPAA compliance. Organizations should consult with qualified legal counsel and compliance professionals."
  const disclaimerLines = doc.splitTextToSize(disclaimer, pageWidth - margin * 2)
  doc.text(disclaimerLines, margin, yPos)

  // Save the PDF
  const filename = `HIPAA-Compliance-Checklist-${contactInfo.organization.replace(/[^a-zA-Z0-9]/g, "-") || "Assessment"}-${assessmentDate.toISOString().split("T")[0]}.pdf`
  doc.save(filename)
}
